/**
 * [TD-47.2a] PlayerPoolWithDrawer - format-agnostic dumb container.
 *
 * Refactored from TD-47.1 auction-specific version to a pure renderer.
 * Format wiring (context hook, drawer component, commit handler) lives
 * in two thin wrappers - `AuctionPlayerPool` + `SnakePlayerPool` -
 * mirroring the 2c.6-C ChatPanel pattern (founder Gap B verdict B.2).
 *
 * v1.2-TD-47.2a deltas vs TD-47.1:
 *   • Auction-specific imports (useDraft, AuctionActionDrawer) removed
 *   • Players list, isMyTurn gate, commit handler, drawer component all
 *     accepted as props - caller controls everything
 *   • Pulse-glow on selected row (founder bundled - `animate-orange-pulse`
 *     class, parity with the on-clock banner)
 *   • State-gating preserved: handleRowClick early-returns on !isMyTurn
 *
 * Props:
 *   players          - Array<{id, name, position, nfl_team, bye_week, projected_points}>
 *   isMyTurn         - boolean; row clicks no-op when false
 *   onClockBanner    - optional ReactNode rendered above the search bar when isMyTurn
 *   selected         - currently-selected player object or null
 *   setSelected      - setter for the selected player
 *   renderDrawer     - fn(player) → ReactNode rendering the format-specific drawer
 *   header           - optional ReactNode rendered in the panel header (default: "Player Pool")
 *   testIdPrefix     - testid prefix ("auction-pool" | "snake-pool"), defaults to "player-pool"
 *   renderRowAddon   - [TD-47.3 Gap 2 verdict A] optional fn(player) → ReactNode
 *                       rendered on the right side of each player row (after PPG).
 *                       Snake passes <QueueButton/>; auction passes nothing.
 *                       [TD-47.4 Gap 10] Suppressed on rows where player.is_drafted.
 *   onDraftedRowClick - [TD-47.4 Gap 6 verdict A] optional fn(player). Fires when
 *                       a row marked is_drafted is clicked. Bypasses the isMyTurn
 *                       gate (Gap 7 confirmed) - analysis is read-only and always
 *                       available regardless of turn state. Format wrapper owns the
 *                       PlayerAnalysisModal mount + state.
 *   getDraftedLabel  - [TD-47.4 Gap 8 verdict A] optional fn(player) → string|null.
 *                       When the row is is_drafted, the returned string replaces the
 *                       sub-line that normally shows "{nfl_team} · Bye {n}". Auction
 *                       wrapper returns "Sold $X to {team_name}"; snake wrapper
 *                       returns "Picked R{round}.{pick} by {team_name}".
 */
import { useState, useEffect, useMemo, useRef } from "react";
import { Search, ArrowUpDown, ChevronDown, TrendingUp, TrendingDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { PlayerAvatar } from "../PlayerHeadshot";
import PositionBadge from "../PositionBadge";
import { useDraftFilters } from "../../contexts/DraftFiltersContext";
import { useEntitlements } from "../../hooks/useEntitlements";

// [Sprint §Part 1] Sort options for the player pool. The paid options
// (PGF best/worst) require entitlement - free users see a lock icon +
// PaywallOverlay via the inline pill.
const SORT_OPTIONS = [
  { key: "adp_best",  label: "ADP · Best first",   paid: false },
  { key: "adp_worst", label: "ADP · Worst first",  paid: false },
  { key: "ppg_hi",    label: "PPG · High to low",  paid: false },
  { key: "ppg_lo",    label: "PPG · Low to high",  paid: false },
  { key: "name_az",   label: "Name · A → Z",       paid: false },
  { key: "name_za",   label: "Name · Z → A",       paid: false },
  { key: "pgf_best",  label: "PGF · Best first",   paid: true },
  { key: "pgf_worst", label: "PGF · Worst first",  paid: true },
];
// ±3 dead zone: rank moves within this window don't show a delta arrow.
const DELTA_DEAD_ZONE = 3;

// [TD-119 v1.4-expanded R4] Dynamic pool filter tabs derived from
// league.settings.roster. Falls back to the standard six positions when no
// league roster context (preserves current behavior for legacy/non-league mounts).
const POSITION_LABELS = {
  qb: "QB", rb: "RB", wr: "WR", te: "TE", k: "K", def: "DEF",
  st: "ST", s: "S", cb: "CB", lb: "LB", dl: "DL", ol: "OL", fb: "FB",
};
const SLOT_KEYS = new Set(["flex", "superflex", "bench"]);
const STANDARD_TABS = ["ALL", "QB", "RB", "WR", "TE", "K", "DEF"];

export function buildPositionTabs(leagueRoster) {
  if (!leagueRoster || typeof leagueRoster !== "object") return STANDARD_TABS;
  const tabs = ["ALL"];
  for (const [key, count] of Object.entries(leagueRoster)) {
    const kl = key.toLowerCase();
    if (SLOT_KEYS.has(kl)) continue;
    if ((count ?? 0) > 0 && POSITION_LABELS[kl]) tabs.push(POSITION_LABELS[kl]);
  }
  return tabs.length > 1 ? tabs : STANDARD_TABS;
}

// [TD-47.6a-ui v1.2 Redline 3 / TD-113 UNBANK] Sleeper CDN thumbnail with
// PlayerAvatar silhouette fallback on error. Lazy-loaded via the browser's
// native `loading="lazy"` attribute to keep scroll-render cost flat.
function PoolRowThumb({ photoUrl, playerId, position }) {
  const [errored, setErrored] = useState(false);
  if (!photoUrl || errored) {
    // [HF] Match the photo's 32px (w-8 h-8) footprint so silhouettes and
    // real headshots align perfectly in the row. PlayerAvatar's default
    // size="sm" renders 44px, which made fallbacks visibly larger than
    // photos in mixed rows. Wrap in a fixed 32px box and shrink the
    // inner avatar via transform/scale to match.
    return (
      <div
        className="w-8 h-8 rounded-full overflow-hidden shrink-0 border border-[#333] bg-[#252525] flex items-center justify-center"
        data-testid={`pool-row-thumb-${playerId}`}
      >
        <div style={{ transform: "scale(0.727)", transformOrigin: "center" }}>
          <PlayerAvatar playerId={playerId} position={position} size="sm" />
        </div>
      </div>
    );
  }
  return (
    <img
      src={photoUrl}
      alt={position || "Player"}
      loading="lazy"
      onError={() => setErrored(true)}
      className="w-8 h-8 rounded-full object-cover border border-[#333] bg-[#252525] shrink-0"
      data-testid={`pool-row-thumb-${playerId}`}
    />
  );
}

export default function PlayerPoolWithDrawer({
  players = [],
  isMyTurn = false,
  onClockBanner = null,
  selected = null,
  setSelected,
  renderDrawer,
  renderRowAddon = null,
  onDraftedRowClick = null,
  onCanvasUpdate = null,
  getDraftedLabel = null,
  header = "Player Pool",
  testIdPrefix = "player-pool",
  autoPositionFilter = null, // optional: override position filter when nomination opens
  leagueRoster = null, // [TD-119 v1.4-expanded R4] derives the position tabs
  onUpgrade = null, // [Sprint §Part 1] Wired to PaywallOverlay via inline pill.
}) {
  const { posFilter: ctxPosFilter, setPosFilter: setCtxPosFilter } = useDraftFilters();
  const [search, setSearch] = useState("");
  // [Sprint §Part 1] Sort state + entitlement gate + delta tracking.
  const { hasDraftAnalytics, hasSeasonAnalytics } = useEntitlements();
  const isPaid = !!(hasDraftAnalytics || hasSeasonAnalytics);
  const [sortKey, setSortKey] = useState("adp_best");
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  // [Sprint §Part 1 - iter57 fix] Sticky flag that flips true when a
  // free-tier user attempts a paid sort. The inline pill then renders
  // continuously (not just during isPgfSort) so the free user has a
  // persistent path back to PaywallOverlay.
  const [paidSortAttempted, setPaidSortAttempted] = useState(false);
  // Previous PGF rank per player_id - used for the animated ▲/▼ delta
  // arrows when the paid PGF sort is applied. Snapshotted the first time
  // the pool renders with PGF data + on every sort change.
  const prevPgfRanksRef = useRef({});

  // Single source of truth: context state. Local position-chip clicks
  // publish back into context so RosterPanel chip + PlayerPool chip stay
  // in lockstep across the unified shell.
  const posFilter = ctxPosFilter || "ALL";
  const setPosFilter = (p) => setCtxPosFilter?.(p);

  // Auto-filter on autoPositionFilter prop change (auction Section 8 carryover)
  useEffect(() => {
    if (autoPositionFilter) setCtxPosFilter?.(autoPositionFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPositionFilter]);

  // [Gap F TD-47.1] Auto-collapse selected player when turn is lost mid-flight.
  useEffect(() => {
    if (!isMyTurn && selected) {
      setSelected?.(null);
    }
  }, [isMyTurn, selected, setSelected]);

  // [E1-CORRECTION Fix 7a] Memoize the filter + slice the rendered list
  // to MAX_VISIBLE_ROWS so search typing stays near-instant. Without
  // this, every keystroke painted ~2500 unvirtualized rows and the UI
  // froze for 10-15s.
  const MAX_VISIBLE_ROWS = 100;
  const { filtered, totalMatches } = useMemo(() => {
    const lowerSearch = search.trim().toLowerCase();
    const out = [];
    for (let i = 0; i < players.length; i++) {
      const p = players[i];
      if (posFilter !== "ALL" && p.position !== posFilter) continue;
      if (lowerSearch && !p.name.toLowerCase().includes(lowerSearch)) continue;
      out.push(p);
    }
    // [Sprint §Part 1] Sort - key drives the comparator. Insufficient-data
    // players always sort LAST for the two PGF sorts (so free-tier
    // fallbacks land in a sensible place too).
    const CMP = {
      adp_best:  (a, b) => (a.adp_rank_position ?? 9999) - (b.adp_rank_position ?? 9999),
      adp_worst: (a, b) => (b.adp_rank_position ?? -1)   - (a.adp_rank_position ?? -1),
      ppg_hi:    (a, b) => (b.projected_points ?? 0) - (a.projected_points ?? 0),
      ppg_lo:    (a, b) => (a.projected_points ?? 0) - (b.projected_points ?? 0),
      name_az:   (a, b) => (a.name || "").localeCompare(b.name || ""),
      name_za:   (a, b) => (b.name || "").localeCompare(a.name || ""),
      pgf_best:  (a, b) => (a.pgf_rank_position ?? 9999) - (b.pgf_rank_position ?? 9999),
      pgf_worst: (a, b) => (b.pgf_rank_position ?? -1)   - (a.pgf_rank_position ?? -1),
    };
    const cmp = CMP[sortKey] || CMP.adp_best;
    if (sortKey === "pgf_best" || sortKey === "pgf_worst") {
      out.sort((a, b) => {
        // Push insufficient-data + no-composite players to the bottom,
        // ordered by ADP within that tail.
        const aInsuf = !!a.insufficient_data || !a.pgf_rank_position;
        const bInsuf = !!b.insufficient_data || !b.pgf_rank_position;
        if (aInsuf && !bInsuf) return 1;
        if (bInsuf && !aInsuf) return -1;
        if (aInsuf && bInsuf) return (a.adp_rank_position ?? 9999) - (b.adp_rank_position ?? 9999);
        return cmp(a, b);
      });
    } else {
      out.sort(cmp);
    }
    return { filtered: out, totalMatches: out.length };
  }, [players, posFilter, search, sortKey]);

  // [Sprint §Part 1] Snapshot the previous PGF rank order every time the
  // PGF sort is applied so we can render ▲/▼ delta arrows. The delta is
  // (previous_pgf_rank − current_pgf_rank); positive → moved up.
  const isPgfSort = sortKey === "pgf_best" || sortKey === "pgf_worst";
  const currentPgfRankMap = useMemo(() => {
    if (!isPgfSort) return {};
    const m = {};
    filtered.forEach((p, idx) => { m[p.id] = idx + 1; });
    return m;
  }, [filtered, isPgfSort]);
  const prevPgfRankMap = prevPgfRanksRef.current;
  // Update snapshot AFTER render for next transition.
  useEffect(() => {
    if (isPgfSort) prevPgfRanksRef.current = currentPgfRankMap;
  }, [isPgfSort, currentPgfRankMap]);

  const visible = filtered.slice(0, MAX_VISIBLE_ROWS);
  const truncated = totalMatches > MAX_VISIBLE_ROWS;

  const handleRowClick = (player) => {
    // [TD-47.6a-ui Gap B.1] Canvas update fires on EVERY row click - drafted
    // OR available - so the Player Analysis Canvas always reflects the most
    // recently clicked player. Runs before the existing drafted/available
    // branch so we don't change established behavior.
    onCanvasUpdate?.(player);

    // [TD-47.4 Gap 7 verdict] Drafted-row click ALWAYS opens analysis modal
    // (bypasses isMyTurn gate - analysis is read-only and always available).
    if (player?.is_drafted) {
      onDraftedRowClick?.(player);
      return;
    }
    if (!isMyTurn) return; // Gap F state-gate preserved for available rows
    setSelected?.(player);
  };

  return (
    <div
      className="bg-[#1C1C1C] border border-[#252525] rounded-2xl flex flex-col h-full overflow-hidden"
      data-testid={`${testIdPrefix}-container`}
    >
      {/* Header */}
      <div className="px-3 py-2.5 border-b border-[#252525] shrink-0 flex items-center justify-between gap-2">
        <span className="font-display font-bold text-xs uppercase tracking-wider text-[#FF6B00]">
          {header}
        </span>
        {/* [Sprint §Part 1] Sort dropdown - paid PGF options gated. */}
        <div className="relative" data-testid={`${testIdPrefix}-sort-wrap`}>
          <button
            type="button"
            onClick={() => setSortMenuOpen((v) => !v)}
            data-testid={`${testIdPrefix}-sort-trigger`}
            className="flex items-center gap-1 px-2 py-1 rounded bg-[#252525] hover:bg-[#2E2E2E] font-body text-[10px] text-white/70 uppercase tracking-wider transition-colors"
          >
            <ArrowUpDown className="w-3 h-3" />
            <span data-testid={`${testIdPrefix}-sort-current`}>
              {SORT_OPTIONS.find((o) => o.key === sortKey)?.label || "Sort"}
            </span>
            <ChevronDown className="w-3 h-3" />
          </button>
          {sortMenuOpen && (
            <div
              className="absolute right-0 top-full mt-1 z-40 min-w-[220px] rounded-lg border border-[#333] bg-[#1c1c1c] shadow-2xl overflow-hidden"
              data-testid={`${testIdPrefix}-sort-menu`}
              onMouseLeave={() => setSortMenuOpen(false)}
            >
              {SORT_OPTIONS.map((opt) => {
                const gated = opt.paid && !isPaid;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    data-testid={`${testIdPrefix}-sort-option-${opt.key}`}
                    onClick={() => {
                      if (gated) {
                        setPaidSortAttempted(true);
                        onUpgrade?.();
                        setSortMenuOpen(false);
                        return;
                      }
                      setSortKey(opt.key);
                      setSortMenuOpen(false);
                    }}
                    className={`block w-full text-left px-3 py-1.5 font-body text-[11px] transition-colors ${
                      sortKey === opt.key ? "bg-[#FF6B00]/10 text-[#FF6B00]" : "text-white/80 hover:bg-white/[0.05]"
                    }`}
                  >
                    {opt.label}
                    {gated && (
                      <span className="ml-2 font-display uppercase text-[9px] tracking-wider text-white/40">
                        Paid
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* [Sprint §Part 1] Free-tier inline pill - appears when the user
          taps a paid PGF sort option. Bridges to the existing
          PaywallOverlay via onUpgrade. */}
      {!isPaid && (isPgfSort || paidSortAttempted) && (
        <button
          type="button"
          data-testid={`${testIdPrefix}-paywall-pill`}
          onClick={() => onUpgrade?.()}
          className="mx-2 mt-2 mb-1 flex items-center justify-center gap-1.5 rounded-full border border-[#FF6B00]/50 bg-[#FF6B00]/10 px-3 py-1.5 font-display font-bold text-[10px] uppercase tracking-wider text-[#FF6B00] hover:bg-[#FF6B00]/20 transition-colors"
        >
          Unlock PGF Analytics
        </button>
      )}

      {onClockBanner}

      {/* Search + position filters */}
      <div className="p-2 border-b border-[#252525] shrink-0">
        <div className="relative mb-2">
          <Search
            size={11}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[rgba(255,255,255,0.35)]"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search players..."
            data-testid={`${testIdPrefix}-search-input`}
            className="w-full bg-[#252525] border border-transparent focus:border-[#FF6B00]/50 text-[#F5F5F5] rounded pl-7 pr-3 py-1.5 text-xs font-body outline-none transition-colors placeholder-[rgba(255,255,255,0.25)]"
          />
        </div>
        <div className="flex gap-1 flex-wrap">
          {buildPositionTabs(leagueRoster).map((pos) => (
            <button
              key={pos}
              onClick={() => setPosFilter(pos)}
              data-testid={`${testIdPrefix}-pos-filter-${pos}`}
              className={`text-xs font-display font-bold uppercase px-2 py-0.5 rounded transition-all ${
                posFilter === pos
                  ? "bg-[#FF6B00] text-white"
                  : "bg-[#252525] text-[rgba(255,255,255,0.45)] hover:bg-[#2E2E2E]"
              }`}
            >
              {pos}
            </button>
          ))}
        </div>
      </div>

      {/* Player list with INLINE-BELOW-ROW drawer (TD-47.1 Gap B verdict B.1) */}
      <div className="flex-1 overflow-y-auto">
        <AnimatePresence initial={false}>
        {visible.map((player) => {
          const isSelected = selected?.id === player.id;
          const isDrafted = !!player.is_drafted;
          const draftedLabel =
            isDrafted && getDraftedLabel ? getDraftedLabel(player) : null;
          // [Sprint §Part 1] ADP: {pos_rank} | PPG: {ppg} subline. Falls
          // back to the legacy "{team} · Bye {n}" if ADP is missing.
          const adpPart = player.adp_rank_position
            ? `ADP: ${player.position}${player.adp_rank_position}`
            : null;
          const ppgVal = player.projected_points != null
            ? (player.projected_points / 17).toFixed(1) : null;
          const ppgPart = ppgVal ? `PPG: ${ppgVal}` : null;
          const adpPpgLine = [adpPart, ppgPart].filter(Boolean).join(" | ");
          const subLine =
            isDrafted && draftedLabel
              ? draftedLabel
              : adpPpgLine
                ? adpPpgLine
                : `${player.nfl_team} · Bye ${player.bye_week}`;
          const showPulse = isSelected && !isDrafted;
          const draftedBgClass = isDrafted ? "bg-[#5A2B2B]" : "";
          const ariaLabel = isDrafted
            ? `View ${player.name} draft details`
            : undefined;
          // Delta arrow - only under PGF sort with a valid previous rank
          // AND move outside the ±3 dead zone.
          let deltaEl = null;
          if (isPgfSort && isPaid) {
            const prev = prevPgfRankMap[player.id];
            const cur = currentPgfRankMap[player.id];
            if (prev && cur) {
              const move = prev - cur;
              if (Math.abs(move) > DELTA_DEAD_ZONE) {
                deltaEl = move > 0 ? (
                  <span
                    className="ml-1 inline-flex items-center gap-0.5 font-mono text-[9px] text-[#00C853]"
                    data-testid={`pool-row-delta-up-${player.id}`}
                  >
                    <TrendingUp className="w-2.5 h-2.5" />{move}
                  </span>
                ) : (
                  <span
                    className="ml-1 inline-flex items-center gap-0.5 font-mono text-[9px] text-[#FF3B30]"
                    data-testid={`pool-row-delta-down-${player.id}`}
                  >
                    <TrendingDown className="w-2.5 h-2.5" />{Math.abs(move)}
                  </span>
                );
              }
            }
          }
          // PGF 0-100 badge - paid-only, only when composite is present.
          const pgfBadge = (isPaid && player.pgf_composite_score != null) ? (
            <span
              className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-[#FF6B00]/15 text-[#FF6B00] border border-[#FF6B00]/30"
              data-testid={`pool-row-pgf-badge-${player.id}`}
            >
              {Math.round(player.pgf_composite_score)}
            </span>
          ) : null;
          return (
            <motion.div
              key={player.id}
              layout={isPgfSort}
              transition={{ layout: { type: "spring", stiffness: 320, damping: 30 } }}
            >
              <div
                onClick={() => handleRowClick(player)}
                role={isDrafted ? "button" : undefined}
                aria-label={ariaLabel}
                data-testid={`${testIdPrefix}-item-${player.id}`}
                data-selected={isSelected ? "true" : undefined}
                data-drafted={isDrafted ? "true" : undefined}
                className={`flex items-center justify-between px-3 py-2 border-b border-[#252525]/50 transition-colors duration-300 ${draftedBgClass} ${
                  isDrafted
                    ? "cursor-pointer"
                    : isMyTurn
                      ? "cursor-pointer hover:bg-[#FF6B00]/10 hover:border-[#FF6B00]/20"
                      : "cursor-default opacity-80"
                } ${
                  /* [TD-47.2a Gap H] Pulse-glow on selected row - parity with
                     animate-orange-pulse used on the on-clock banner. */
                  showPulse ? "bg-[#FF6B00]/20 animate-orange-pulse" : ""
                }`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  {/* [TD-47.6a-ui v1.2 Redline 3 / TD-113 UNBANK] Sleeper CDN
                      thumbnail with PlayerAvatar silhouette fallback on error.
                      Native lazy loading keeps render cost flat under scroll. */}
                  <PoolRowThumb
                    photoUrl={player.photo_url}
                    playerId={player.id}
                    position={player.position}
                  />
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 min-w-0">
                      {/* [E1-CORRECTION Fix 1] Position badge inline with name. */}
                      <PositionBadge position={player.position} size="sm" />
                      <p
                        className="font-body text-xs text-[#F5F5F5] truncate font-medium"
                        data-testid={`${testIdPrefix}-row-name-${player.id}`}
                      >
                        {player.name}
                      </p>
                    </div>
                    <p
                      className="font-body text-xs text-[rgba(255,255,255,0.40)]"
                      data-testid={
                        isDrafted
                          ? `${testIdPrefix}-drafted-label-${player.id}`
                          : undefined
                      }
                    >
                      {subLine}
                    </p>
                  </div>
                </div>
                <div className="text-right shrink-0 ml-2 flex items-center gap-1.5">
                  {deltaEl}
                  {pgfBadge}
                  <div className="w-14">
                    <p
                      className="font-mono font-bold text-xs text-[#F5F5F5]"
                      data-testid={`ppg-${player.id}`}
                    >
                      {player.projected_points != null
                        ? (player.projected_points / 17).toFixed(1)
                        : "-"}
                    </p>
                    <p className="font-body text-xs text-[rgba(255,255,255,0.35)]">
                      PPG
                    </p>
                  </div>
                </div>
                {/* [TD-47.4 Gap 10] Suppress addon on drafted rows. */}
                {renderRowAddon && !isDrafted ? (
                  <div
                    className="ml-2 shrink-0"
                    data-testid={`${testIdPrefix}-row-addon-${player.id}`}
                  >
                    {renderRowAddon(player)}
                  </div>
                ) : null}
              </div>
              {isSelected && !isDrafted && renderDrawer?.(player)}
            </motion.div>
          );
        })}
        </AnimatePresence>
        {truncated && (
          <div
            data-testid={`${testIdPrefix}-truncated-notice`}
            className="px-3 py-2 text-center font-body text-[10px] text-[rgba(255,255,255,0.40)]"
          >
            Showing first {MAX_VISIBLE_ROWS} of {totalMatches} matches. Refine your search.
          </div>
        )}
      </div>
    </div>
  );
}
